import { Editor } from '@tiptap/core'

import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area'
import { Separator } from '@/components/ui/separator'
import { TooltipProvider } from '@/components/ui/tooltip'

import { AlignmentTooolbar } from './alignment'
import { BlockquoteToolbar } from './blockquote'
import { BoldToolbar } from './bold'
import { BulletListToolbar } from './bullet-list'
import { CodeToolbar } from './code'
import { CodeBlockToolbar } from './code-block'
import { ColorHighlightToolbar } from './color-and-highlight'
import { DirectionToolbar } from './direction'
import { HeadingsToolbar } from './headings'
import { HorizontalRuleToolbar } from './horizontal-rule'
import { ItalicToolbar } from './italic'
import { LinkToolbar } from './link'
import { OrderedListToolbar } from './ordered-list'
import { RedoToolbar } from './redo'
import { SearchAndReplaceToolbar } from './search-and-replace-toolbar'
import { StrikeThroughToolbar } from './strikethrough'
import {
  hasEditorCommand,
  hasEditorExtension,
  ToolbarProvider
} from './toolbar-provider'
import { UnderlineToolbar } from './underline'
import { UndoToolbar } from './undo'

export const EditorToolbar = ({ editor }: { editor: Editor }) => {
  const hasUndo = hasEditorCommand(editor, 'undo')
  const hasRedo = hasEditorCommand(editor, 'redo')
  const hasHeading = hasEditorExtension(editor, 'heading')
  const hasBlockquote = hasEditorExtension(editor, 'blockquote')
  const hasCode = hasEditorExtension(editor, 'code')
  const hasCodeBlock = hasEditorExtension(editor, 'codeBlock')
  const hasBold = hasEditorExtension(editor, 'bold')
  const hasItalic = hasEditorExtension(editor, 'italic')
  const hasUnderline = hasEditorExtension(editor, 'underline')
  const hasStrike = hasEditorExtension(editor, 'strike')
  const hasLink = hasEditorExtension(editor, 'link')
  const hasBulletList = hasEditorExtension(editor, 'bulletList')
  const hasOrderedList = hasEditorExtension(editor, 'orderedList')
  const hasHorizontalRule = hasEditorExtension(editor, 'horizontalRule')
  const hasTextAlign = hasEditorExtension(editor, 'textAlign')
  const hasTextDirection = hasEditorExtension(editor, 'textDirection')
  const hasColor = hasEditorExtension(editor, 'color')
  const hasHighlight = hasEditorExtension(editor, 'highlight')

  const hasHistoryGroup = hasUndo || hasRedo
  const hasStructureGroup =
    hasHeading || hasBlockquote || hasCode || hasCodeBlock
  const hasFormattingGroup =
    hasBold || hasItalic || hasUnderline || hasStrike || hasLink
  const hasListsGroup = hasBulletList || hasOrderedList || hasHorizontalRule
  const hasAlignmentGroup = hasTextAlign
  const hasDirectionGroup = hasTextDirection
  const hasStyleGroup = hasColor || hasHighlight
  const canUseSearchAndReplace =
    hasEditorExtension(editor, 'searchAndReplace') &&
    hasEditorCommand(editor, 'setSearchTerm') &&
    hasEditorCommand(editor, 'setReplaceTerm')

  return (
    <div className="bg-background sticky top-0 hidden w-full border-b sm:block">
      <ToolbarProvider editor={editor}>
        <TooltipProvider>
          <ScrollArea className="h-fit py-0.5">
            <div>
              <div className="flex items-center gap-1 px-2">
                {/* History Group */}
                {hasHistoryGroup && (
                  <>
                    {hasUndo && <UndoToolbar />}
                    {hasRedo && <RedoToolbar />}
                    <Separator className="mx-1 h-7" orientation="vertical" />
                  </>
                )}

                {/* Text Structure Group */}
                {hasStructureGroup && (
                  <>
                    {hasHeading && <HeadingsToolbar />}
                    {hasBlockquote && <BlockquoteToolbar />}
                    {hasCode && <CodeToolbar />}
                    {hasCodeBlock && <CodeBlockToolbar />}
                    <Separator className="mx-1 h-7" orientation="vertical" />
                  </>
                )}

                {/* Basic Formatting Group */}
                {hasFormattingGroup && (
                  <>
                    {hasBold && <BoldToolbar />}
                    {hasItalic && <ItalicToolbar />}
                    {hasUnderline && <UnderlineToolbar />}
                    {hasStrike && <StrikeThroughToolbar />}
                    {hasLink && <LinkToolbar />}
                    <Separator className="mx-1 h-7" orientation="vertical" />
                  </>
                )}

                {/* Lists & Structure Group */}
                {hasListsGroup && (
                  <>
                    {hasBulletList && <BulletListToolbar />}
                    {hasOrderedList && <OrderedListToolbar />}
                    {hasHorizontalRule && <HorizontalRuleToolbar />}
                    <Separator className="mx-1 h-7" orientation="vertical" />
                  </>
                )}

                {/* Alignment Group */}
                {hasAlignmentGroup && (
                  <>
                    <AlignmentTooolbar />
                    <Separator className="mx-1 h-7" orientation="vertical" />
                  </>
                )}

                {/* Direction Group */}
                {hasDirectionGroup && (
                  <>
                    <DirectionToolbar />
                    <Separator className="mx-1 h-7" orientation="vertical" />
                  </>
                )}

                {/* Media & Styling Group */}
                {hasStyleGroup && (
                  <>
                    <ColorHighlightToolbar />
                    {canUseSearchAndReplace && (
                      <Separator className="mx-1 h-7" orientation="vertical" />
                    )}
                  </>
                )}

                <div className="flex-1" />

                {/* Utility Group */}
                {canUseSearchAndReplace && <SearchAndReplaceToolbar />}
              </div>
            </div>
            <ScrollBar className="hidden" orientation="horizontal" />
          </ScrollArea>
        </TooltipProvider>
      </ToolbarProvider>
    </div>
  )
}
